import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMessageStore } from '../../store/messageStore';
import { useServerStore } from '../../store/serverStore';
import { useAuthStore } from '../../store/authStore';
import { startTyping, stopTyping } from '../../lib/realtime';
import { Send, Paperclip, Smile, X } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { messagesAPI } from '../../lib/api';
import {
  ApplicationCommand,
  ApplicationCommandOption,
  CommandOptionType,
  SlashChoice,
  SlashCommandDraft,
  clearOptionValue,
  createSlashDraft,
  displayOptionValue,
  getEffectiveOptions,
  getFocusedOption,
  getNextFocusableOption,
  getPreviousFilledOptionName,
  getSubcommandChoices,
  optionAllowsFreeText,
  optionCandidates,
  optionNeedsSelection,
  serializeSlashDraft,
  setOptionValue,
  setSubcommandPath,
  validateOptionInput,
  validateSlashDraft,
} from '../../lib/slashCommands';

interface MessageInputProps {
  channelId: string;
  replyTo?: string | null;
  initialValue?: string;
  onSend?: (content: string) => void;
  onCancel?: () => void;
  isEditing?: boolean;
  isDm?: boolean;
  recipientId?: string;
  disabled?: boolean;
  disabledReason?: string;
  canAttachFiles?: boolean;
}

interface User {
  id: string;
  username: string;
  avatar_url?: string;
  status?: string;
}

const MessageInput: React.FC<MessageInputProps> = ({ 
  channelId, 
  replyTo = null, 
  initialValue = '', 
  onSend,
  onCancel,
  isEditing = false,
  isDm = false,
  recipientId,
  disabled = false,
  disabledReason,
  canAttachFiles = true,
}) => {
  const [message, setMessage] = useState(initialValue);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [slashCommands, setSlashCommands] = useState<ApplicationCommand[]>([]);
  const [showSlashCommands, setShowSlashCommands] = useState(false);
  const [activeSlashIndex, setActiveSlashIndex] = useState(0);
  const [selectedCommand, setSelectedCommand] = useState<ApplicationCommand | null>(null);
  const [commandDraft, setCommandDraft] = useState<SlashCommandDraft | null>(null);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [autocompleteChoices, setAutocompleteChoices] = useState<SlashChoice[]>([]);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const autocompleteInteractionRef = useRef<string | null>(null);
  
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const slashInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  
  const { currentServer, members, channels, roles } = useServerStore();
  const { user, profile } = useAuthStore();
  
  useEffect(() => {
    // Focus input when component mounts
    inputRef.current?.focus();
    
    // Auto-resize textarea
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, []);
  
  useEffect(() => {
    // Handle clicks outside emoji picker
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
      
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowMentionSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  useEffect(() => {
    // Rechercher les suggestions lorsque mentionQuery change
    if (mentionQuery.length > 0 && currentServer) {
      const filteredMembers = members
        .filter(member => member.user?.username.toLowerCase().includes(mentionQuery.toLowerCase()))
        .map(member => ({
          id: member.user_id,
          username: member.user?.username || '',
          avatar_url: member.user?.avatar_url || undefined,
          status: member.user?.status || undefined
        }));
      
      setSuggestions(filteredMembers.slice(0, 10));
      setActiveSuggestionIndex(0);
    }
  }, [mentionQuery, members, currentServer]);

  useEffect(() => {
    if (isDm || isEditing || commandDraft || !message.startsWith('/') || !channelId) {
      setShowSlashCommands(false);
      return;
    }

    let cancelled = false;
    messagesAPI.getChannelApplicationCommands(channelId)
      .then((commands: ApplicationCommand[]) => {
        if (cancelled) return;
        const chatInputCommands = (Array.isArray(commands) ? commands : []).filter((command) => (command.type ?? 1) === 1);
        setSlashCommands(chatInputCommands);
        setShowSlashCommands(chatInputCommands.length > 0 && !selectedCommand);
        setActiveSlashIndex(0);
      })
      .catch(() => {
        if (!cancelled) setSlashCommands([]);
      });

    return () => {
      cancelled = true;
    };
  }, [channelId, isDm, isEditing, commandDraft, message.startsWith('/')]);

  useEffect(() => {
    const handleInteractionResponse = (event: Event) => {
      const payload = (event as CustomEvent).detail;
      if (!payload || payload.type !== 'autocomplete') return;
      if (payload.interaction_id !== autocompleteInteractionRef.current) return;
      const choices = payload.data?.choices || [];
      setAutocompleteChoices(
        choices.map((choice: any) => ({
          id: String(choice.value),
          label: choice.name || String(choice.value),
          value: choice.value,
          type: commandDraft ? getActiveOption(commandDraft)?.type : undefined,
        })),
      );
    };
    window.addEventListener('erozion-interaction-response', handleInteractionResponse);
    return () => window.removeEventListener('erozion-interaction-response', handleInteractionResponse);
  }, [commandDraft]);

  useEffect(() => {
    if (!commandDraft || !channelId) return;
    const focused = getActiveOption(commandDraft);
    if (!focused?.autocomplete) {
      setAutocompleteChoices([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await messagesAPI.createChannelInteraction(channelId, {
          application_id: commandDraft.command.application_id,
          command_id: commandDraft.command.id,
          type: 4,
          data: {
            id: commandDraft.command.id,
            name: commandDraft.command.name,
            type: commandDraft.command.type ?? 1,
            options: buildFocusedOptions(commandDraft, focused, message),
          },
        });
        autocompleteInteractionRef.current = response?.id || null;
      } catch {
        setAutocompleteChoices([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [channelId, commandDraft, message]);
  
  const emitStopTyping = useCallback(() => {
    if (isTypingRef.current) {
      stopTyping(channelId, isDm, recipientId);
      isTypingRef.current = false;
    }
  }, [channelId, isDm, recipientId]);

  // Cleanup typing on unmount or channel change
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (isTypingRef.current) stopTyping(channelId, isDm, recipientId);
    };
  }, [channelId, isDm, recipientId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setMessage(newValue);
    
    // Auto-resize textarea
    e.target.style.height = 'auto';
    const isMultiLine = e.target.scrollHeight > 40;
    e.target.style.lineHeight = isMultiLine ? '20px' : '40px';
    e.target.style.padding = isMultiLine ? '10px 0' : '0';
    e.target.style.height = `${e.target.scrollHeight}px`;
    
    // Détecter les mentions
    detectMention(newValue, e.target.selectionStart);
    if (commandDraft) {
      setCommandError(null);
      return;
    }
    if (!newValue.startsWith('/')) {
      setSelectedCommand(null);
      setCommandDraft(null);
      setShowSlashCommands(false);
    } else if (!selectedCommand) {
      setShowSlashCommands(slashCommands.length > 0);
    }

    // Typing indicator
    if (!isEditing && newValue.trim()) {
      if (!isTypingRef.current) {
        startTyping(channelId, isDm, recipientId);
        isTypingRef.current = true;
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(emitStopTyping, 3000);
    } else {
      emitStopTyping();
    }
  };

  const handleSlashInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
    setCommandError(null);
    setActiveOptionIndex(0);
  };
  
  const detectMention = (text: string, cursorPosition: number) => {
    // Trouver le dernier @ avant le curseur
    const textToCursor = text.slice(0, cursorPosition);
    const lastAtSymbolIndex = textToCursor.lastIndexOf('@');
    
    if (lastAtSymbolIndex !== -1) {
      // Vérifier s'il y a un espace entre le @ et le curseur
      const textBetweenAtAndCursor = textToCursor.slice(lastAtSymbolIndex + 1);
      
      // Si pas d'espace, c'est potentiellement une mention
      if (!textBetweenAtAndCursor.includes(' ')) {
        setMentionQuery(textBetweenAtAndCursor);
        setMentionStart(lastAtSymbolIndex);
        setShowMentionSuggestions(true);
        return;
      }
    }
    
    // Sinon, pas de mention
    setShowMentionSuggestions(false);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (commandDraft) {
      if (e.key === 'Escape') {
        e.preventDefault();
        clearSlashDraft();
      } else if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        if (!commitFocusedOption()) {
          handleSendMessage();
        }
      }
    } else if (e.key === ' ' && message.startsWith('/') && !message.includes(' ')) {
      const exactCommand = slashCommands.find((command) => command.name.toLowerCase() === message.slice(1).toLowerCase());
      if (exactCommand) {
        e.preventDefault();
        selectSlashCommand(exactCommand);
      }
    } else if (showSlashCommands && filteredSlashCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSlashIndex((prev) => Math.min(prev + 1, filteredSlashCommands.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSlashIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        selectSlashCommand(filteredSlashCommands[activeSlashIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowSlashCommands(false);
      }
    } else if (showMentionSuggestions && suggestions.length > 0) {
      // Navigation dans les suggestions
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => 
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => 
          prevIndex > 0 ? prevIndex - 1 : 0
        );
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        // Sélectionner la suggestion
        e.preventDefault();
        insertMention(suggestions[activeSuggestionIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionSuggestions(false);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSlashKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!commandDraft) return;
    const suggestions = getActiveSlashSuggestions();
    if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault();
      setActiveOptionIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setActiveOptionIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      clearSlashDraft();
      return;
    }
    if (e.key === 'Backspace' && message === '') {
      const previous = getPreviousFilledOptionName(commandDraft);
      e.preventDefault();
      if (previous) {
        setCommandDraft(clearOptionValue(commandDraft, previous));
      } else {
        clearSlashDraft();
      }
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      commitFocusedOption();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const requirementsMet = validateSlashDraft(commandDraft) === null;
      if (message.trim()) {
        const focused = getActiveOption(commandDraft);
        const mode = focused && optionAllowsFreeText(focused) && !optionNeedsSelection(focused) ? 'typed' : 'selected';
        commitFocusedOption(mode);
      } else if (requirementsMet) {
        void executeSlashDraft(false);
      } else if (suggestions.length > 0 || getSubcommandChoices(commandDraft.command, commandDraft.path).length > 0) {
        commitFocusedOption('selected');
      } else {
        void executeSlashDraft(false);
      }
    }
  };
  
  const insertMention = (user: User) => {
    if (mentionStart === -1) return;
    
    // Remplacer @query par @username
    const before = message.substring(0, mentionStart);
    const after = message.substring(mentionStart + mentionQuery.length + 1); // +1 pour le @
    const newMessage = `${before}@${user.username} ${after}`;
    
    setMessage(newMessage);
    setShowMentionSuggestions(false);
    
    // Remettre le focus et placer le curseur
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = mentionStart + user.username.length + 2; // +2 pour @ et espace
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };
  
  const handleSendMessage = () => {
    if (disabled) return;
    if (!message.trim() && !commandDraft) return;
    
    // Stop typing on send
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    emitStopTyping();

    if (commandDraft && !isDm && !isEditing) {
      void executeSlashDraft();
      return;
    }

    const typedSlashCommand = !selectedCommand && message.startsWith('/')
      ? slashCommands.find((command) => command.name.toLowerCase() === slashQuery)
      : null;
    if ((selectedCommand || typedSlashCommand) && !isDm && !isEditing) {
      const command = selectedCommand || typedSlashCommand!;
      const trailing = message.trim().startsWith(`/${command.name}`)
        ? message.trim().slice(command.name.length + 1).trim()
        : '';
      void executeCommandDirect(command, trailing);
      return;
    }

    if (isEditing) {
      onSend?.(message);
    } else if (onSend && isDm) {
      onSend(message);
    } else {
      useMessageStore.getState().sendMessage(message, channelId, replyTo, currentServer?.id);
      onSend?.(message);
    }
    
    setMessage('');
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.lineHeight = '40px';
      inputRef.current.style.padding = '0';
    }
  };
  
  const handleEmojiClick = (emojiData: any) => {
    setMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  const slashQuery = message.startsWith('/') ? message.slice(1).split(/\s+/)[0]?.toLowerCase() || '' : '';
  const filteredSlashCommands = slashCommands
    .filter((command) => command.name.toLowerCase().includes(slashQuery))
    .slice(0, 10);

  const getSlashInputWidth = (placeholder = '') => {
    const length = Math.max((message || placeholder || '').length, 1);
    return `${Math.min(420, Math.max(80, (length + 1) * 8))}px`;
  };

  const selectSlashCommand = (command: ApplicationCommand) => {
    setSelectedCommand(command);
    setCommandDraft(createSlashDraft(command));
    setCommandError(null);
    setAutocompleteChoices([]);
    setActiveOptionIndex(0);
    setShowSlashCommands(false);
    setMessage('');
    setTimeout(() => slashInputRef.current?.focus(), 0);
  };

  const clearSlashDraft = () => {
    setSelectedCommand(null);
    setCommandDraft(null);
    setCommandError(null);
    setAutocompleteChoices([]);
    setActiveOptionIndex(0);
    setMessage('');
    setShowSlashCommands(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const getActiveOption = (draft: SlashCommandDraft): ApplicationCommandOption | null => {
    const focused = getFocusedOption(draft);
    if (!focused) return null;
    if (draft.values[focused.name] === undefined) return focused;
    return getNextFocusableOption(draft, focused.name);
  };

  const getActiveSlashSuggestions = () => {
    if (!commandDraft) return [] as Array<ApplicationCommandOption | SlashChoice>;
    const subcommands = getSubcommandChoices(commandDraft.command, commandDraft.path);
    if (subcommands.length > 0) {
      const query = message.trim().toLowerCase();
      return subcommands.filter((option) => !query || option.name.toLowerCase().includes(query));
    }
    const focused = getActiveOption(commandDraft);
    if (!focused || focused.type === CommandOptionType.Attachment) return [];
    return getCurrentOptionCandidates(focused);
  };

  const nextDraftWithFocusedValue = (draft: SlashCommandDraft, mode: 'typed' | 'selected'): SlashCommandDraft | null => {
    const subcommands = getSubcommandChoices(draft.command, draft.path);
    if (subcommands.length > 0) {
      const query = message.trim().toLowerCase();
      const matches = subcommands.filter((option) => !query || option.name.toLowerCase().includes(query));
      const choice = matches[Math.min(activeOptionIndex, Math.max(matches.length - 1, 0))];
      if (!choice) return null;
      return setSubcommandPath(draft, choice);
    }

    const focused = getActiveOption(draft);
    if (!focused) return null;
    const candidates = getCurrentOptionCandidates(focused);
    if (mode === 'selected' && candidates.length > 0) {
      const choice = candidates[Math.min(activeOptionIndex, candidates.length - 1)];
      return setOptionValue(draft, focused, choice.value, choice.label);
    }
    if (optionNeedsSelection(focused)) {
      setCommandError(`Choose a valid value for ${focused.name}.`);
      return null;
    }
    const validation = validateOptionInput(focused, message);
    if (validation) {
      setCommandError(validation);
      return null;
    }
    if (!message.trim() && !focused.required) return null;
    return setOptionValue(draft, focused, message.trim(), message.trim());
  };

  const commitFocusedOption = (mode: 'typed' | 'selected' = 'selected') => {
    if (!commandDraft) return false;
    const nextDraft = nextDraftWithFocusedValue(commandDraft, mode);
    if (!nextDraft) return false;
    setCommandDraft(nextDraft);
    setCommandError(null);
    setAutocompleteChoices([]);
    setActiveOptionIndex(0);
    setMessage('');
    setTimeout(() => slashInputRef.current?.focus(), 0);
    return true;
  };

  const applyOptionChoice = (option: ApplicationCommandOption, choice: SlashChoice) => {
    if (!commandDraft) return;
    const nextDraft = setOptionValue(commandDraft, option, choice.value, choice.label);
    setCommandDraft(nextDraft);
    setCommandError(null);
    setAutocompleteChoices([]);
    setActiveOptionIndex(0);
    setMessage('');
    setTimeout(() => slashInputRef.current?.focus(), 0);
  };

  const executeSlashDraft = async (commitTypedValue = true) => {
    if (!commandDraft) return;
    let nextDraft = commandDraft;
    if (commitTypedValue && message.trim()) {
      const focused = getActiveOption(commandDraft);
      if (focused && optionNeedsSelection(focused)) {
        setCommandError(`Choose a valid value for ${focused.name}.`);
        slashInputRef.current?.focus();
        return;
      }
      const committed = focused && optionAllowsFreeText(focused)
        ? nextDraftWithFocusedValue(commandDraft, 'typed')
        : null;
      if (committed) {
        nextDraft = committed;
        setCommandDraft(committed);
        setMessage('');
      }
    }
    const error = validateSlashDraft(nextDraft);
    if (error) {
      setCommandError(error);
      slashInputRef.current?.focus();
      return;
    }

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    emitStopTyping();

    try {
      const response = await messagesAPI.createChannelInteraction(channelId, {
        application_id: nextDraft.command.application_id,
        command_id: nextDraft.command.id,
        type: 2,
        data: {
          id: nextDraft.command.id,
          name: nextDraft.command.name,
          type: nextDraft.command.type ?? 1,
          options: serializeSlashDraft(nextDraft),
        },
      });
      emitPendingInteraction(response, nextDraft);
      clearSlashDraft();
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.lineHeight = '40px';
        inputRef.current.style.padding = '0';
      }
    } catch (error) {
      console.error('Failed to execute slash command', error);
    }
  };

  const executeCommandDirect = async (command: ApplicationCommand, trailing = '') => {
    const draft = createSlashDraft(command);
    const error = validateSlashDraft(draft);
    if (error) {
      setSelectedCommand(command);
      setCommandDraft(draft);
      setCommandError(trailing ? null : error);
      setMessage(trailing);
      setTimeout(() => slashInputRef.current?.focus(), 0);
      return;
    }
    try {
      const response = await messagesAPI.createChannelInteraction(channelId, {
        application_id: command.application_id,
        command_id: command.id,
        type: 2,
        data: {
          id: command.id,
          name: command.name,
          type: command.type ?? 1,
          options: serializeSlashDraft(draft),
        },
      });
      emitPendingInteraction(response, draft);
      clearSlashDraft();
    } catch (error) {
      console.error('Failed to execute slash command', error);
    }
  };

  const emitPendingInteraction = (response: any, draft: SlashCommandDraft) => {
    if (!response?.id) return;
    const commandDisplay = response.command_display || `/${draft.command.name}${draft.path.length ? ` ${draft.path.join(' ')}` : ''}`;
    window.dispatchEvent(new CustomEvent('erozion-interaction-pending', {
      detail: {
        id: `pending-${response.id}`,
        interaction_id: response.id,
        channel_id: channelId,
        created_at: response.created_at || new Date().toISOString(),
        command_name: response.command_name || draft.command.name,
        command_display: commandDisplay,
        application: draft.command.application,
        interaction_metadata: {
          id: response.id,
          type: 2,
          name: response.command_name || draft.command.name,
          command_display: commandDisplay,
          application_id: draft.command.application_id,
          command_id: draft.command.id,
          channel_id: channelId,
          user: {
            id: user?.id,
            user_id: user?.id,
            username: user?.username,
            display_name: profile?.display_name || user?.display_name || user?.username,
            avatar_url: profile?.avatar_url || user?.avatar,
          },
        },
      },
    }));
  };

  const getCurrentOptionCandidates = (option: ApplicationCommandOption) => {
    if (option.autocomplete && autocompleteChoices.length > 0) return autocompleteChoices;
    return optionCandidates(option, { members, channels, roles }, message.trim());
  };

  const buildFocusedOptions = (draft: SlashCommandDraft, focused: ApplicationCommandOption, rawValue: string) => {
    const base = serializeSlashDraft(draft);
    const focusedPayload = { type: focused.type, name: focused.name, value: rawValue, focused: true };
    if (draft.path.length === 2) {
      const group = base[0] || { type: CommandOptionType.SubcommandGroup, name: draft.path[0], options: [] };
      const sub = group.options?.[0] || { type: CommandOptionType.Subcommand, name: draft.path[1], options: [] };
      sub.options = [...(sub.options || []).filter((option: any) => option.name !== focused.name), focusedPayload];
      group.options = [sub];
      return [group];
    }
    if (draft.path.length === 1) {
      const sub = base[0] || { type: CommandOptionType.Subcommand, name: draft.path[0], options: [] };
      sub.options = [...(sub.options || []).filter((option: any) => option.name !== focused.name), focusedPayload];
      return [sub];
    }
    return [...base.filter((option: any) => option.name !== focused.name), focusedPayload];
  };

  const renderSlashSuggestions = () => {
    if (!commandDraft) return null;
    const subcommands = getSubcommandChoices(commandDraft.command, commandDraft.path);
    const focused = getActiveOption(commandDraft);
    const suggestions = getActiveSlashSuggestions();
    const showSuggestions = suggestions.length > 0;

    return (
      <div className="absolute bottom-full left-0 right-0 mb-2 rounded-lg border border-border-color bg-bg-secondary shadow-xl z-20 overflow-hidden">
        <div className="px-3 py-2 border-b border-border-color flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-text-primary truncate">
              /{commandDraft.command.name}{commandDraft.path.length > 0 ? ` ${commandDraft.path.join(' ')}` : ''}
            </div>
            <div className="text-xs text-text-muted truncate">
              {subcommands.length > 0
                ? 'Choose a subcommand'
                : focused
                  ? `${focused.name}${focused.required ? ' is required' : ' is optional'}${focused.description ? ` · ${focused.description}` : ''}`
                  : 'Ready to send'}
            </div>
          </div>
          {commandError && <div className="text-xs text-error flex-shrink-0">{commandError}</div>}
        </div>

        {showSuggestions && (
          <div className="max-h-56 overflow-y-auto py-1">
            {suggestions.map((suggestion, index) => {
              const isSubcommand = 'options' in suggestion || suggestion.type === CommandOptionType.Subcommand || suggestion.type === CommandOptionType.SubcommandGroup;
              return isSubcommand ? (
                <button
                  key={(suggestion as ApplicationCommandOption).name}
                  type="button"
                  onClick={() => {
                    setCommandDraft(setSubcommandPath(commandDraft, suggestion as ApplicationCommandOption));
                    setMessage('');
                    setActiveOptionIndex(0);
                    setTimeout(() => slashInputRef.current?.focus(), 0);
                  }}
                  onMouseEnter={() => setActiveOptionIndex(index)}
                  className={`w-full px-3 py-2 text-left transition-colors ${index === activeOptionIndex ? 'bg-highlight text-text-primary' : 'hover:bg-highlight/60'}`}
                >
                  <div className="text-sm font-semibold text-text-primary">{(suggestion as ApplicationCommandOption).name}</div>
                  <div className="text-xs text-text-muted">{(suggestion as ApplicationCommandOption).description || 'Subcommand'}</div>
                </button>
              ) : (
                <button
                  key={(suggestion as SlashChoice).id}
                  type="button"
                  onClick={() => focused && applyOptionChoice(focused, suggestion as SlashChoice)}
                  onMouseEnter={() => setActiveOptionIndex(index)}
                  className={`w-full px-3 py-2 text-left transition-colors ${index === activeOptionIndex ? 'bg-highlight text-text-primary' : 'hover:bg-highlight/60'}`}
                >
                  <div className="text-sm font-semibold text-text-primary">{(suggestion as SlashChoice).label}</div>
                  <div className="text-xs text-text-muted">{String((suggestion as SlashChoice).value)}</div>
                </button>
              );
            })}
          </div>
        )}

        {!showSuggestions && focused?.type === CommandOptionType.Attachment && (
          <div className="px-3 py-3 text-sm text-text-muted">
            Attachment arguments are not supported in this composer yet.
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="relative">
      <div className="flex items-center bg-bg-tertiary/50 border border-border-color rounded-xl shadow-inner-light transition-colors focus-within:border-accent-color/30">
        {canAttachFiles && (
          <button
            type="button"
            className="flex-shrink-0 pl-3 pr-1 text-text-muted hover:text-text-primary transition-colors"
            title="Pièce jointe"
            disabled={disabled}
          >
            <Paperclip className="h-5 w-5" />
          </button>
        )}

        {commandDraft ? (
          <div className="flex-1 min-h-[40px] flex flex-wrap items-center gap-1.5 px-1 py-1.5 text-[15px]">
            <button
              type="button"
              onClick={clearSlashDraft}
              className="h-7 rounded-md bg-accent-color/15 px-2 font-semibold text-accent-color"
              title="Cancel command"
            >
              /{commandDraft.command.name}
            </button>
            {commandDraft.path.map((segment) => (
              <span key={segment} className="h-7 inline-flex items-center rounded-md bg-highlight px-2 font-medium text-text-primary">
                {segment}
              </span>
            ))}
            {getEffectiveOptions(commandDraft.command, commandDraft.path)
              .filter((option) => commandDraft.values[option.name] !== undefined)
              .map((option) => (
                <button
                  key={option.name}
                  type="button"
                  onClick={() => {
                    setMessage(displayOptionValue(commandDraft, option));
                    setCommandDraft(clearOptionValue(commandDraft, option.name));
                    setActiveOptionIndex(0);
                    setTimeout(() => slashInputRef.current?.focus(), 0);
                  }}
                  className="h-7 rounded-md bg-highlight px-2 text-text-primary"
                >
                  <span className="text-text-muted">{option.name}:</span> {displayOptionValue(commandDraft, option)}
                  <span
                    className="ml-1 text-text-muted hover:text-text-primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      setCommandDraft(clearOptionValue(commandDraft, option.name));
                      setMessage('');
                    }}
                  >
                    ×
                  </span>
                </button>
              ))}
            {(() => {
              const focused = getActiveOption(commandDraft);
              return focused ? (
                <label className="max-w-full h-8 inline-flex items-center rounded-md bg-bg-secondary/80 border border-accent-color/30 px-2">
                  <span className="mr-1.5 text-accent-color font-semibold whitespace-nowrap">{focused.name}:</span>
                  <input
                    ref={slashInputRef}
                    value={message}
                    onChange={handleSlashInputChange}
                    onKeyDown={handleSlashKeyDown}
                    placeholder={focused.description || (focused.required ? 'required' : 'optional')}
                    style={{ width: getSlashInputWidth(focused.description || (focused.required ? 'required' : 'optional')) }}
                    className="bg-transparent border-0 p-0 text-text-primary placeholder-text-muted focus:outline-none focus:ring-0"
                  />
                </label>
              ) : (
                <label className="max-w-full h-8 inline-flex items-center rounded-md bg-bg-secondary/50 px-2">
                  <input
                    ref={slashInputRef}
                    value={message}
                    onChange={handleSlashInputChange}
                    onKeyDown={handleSlashKeyDown}
                    placeholder="Press Enter to send"
                    style={{ width: getSlashInputWidth('Press Enter to send') }}
                    className="bg-transparent border-0 p-0 text-text-primary placeholder-text-muted focus:outline-none focus:ring-0"
                  />
                </label>
              );
            })()}
          </div>
        ) : (
          <textarea
            ref={inputRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? (disabledReason || "You don't have permission to send messages in this channel.") : isEditing ? "Modifier votre message..." : "Envoyer un message..."}
            disabled={disabled}
            className="flex-1 bg-transparent border-0 max-h-32 px-1 text-text-primary placeholder-text-muted focus:ring-0 resize-none focus:outline-none text-[15px] disabled:cursor-not-allowed disabled:opacity-70"
            style={{ minHeight: '40px', lineHeight: '40px' }}
            rows={1}
          />
        )}
        
        <div className="flex items-center px-1.5 gap-1">
          <button
            type="button"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-all"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            <Smile className="h-5 w-5" />
          </button>
          
          <button
            type="button"
            onClick={handleSendMessage}
            disabled={disabled || (!message.trim() && !commandDraft)}
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-accent-color hover:bg-accent-hover text-black disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>

      {renderSlashSuggestions()}

      {showSlashCommands && filteredSlashCommands.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-2 max-h-72 overflow-y-auto rounded-lg border border-border-color bg-bg-secondary shadow-xl z-20">
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-text-muted border-b border-border-color">
            Commandes
          </div>
          {filteredSlashCommands.map((command, index) => (
            <button
              key={command.id}
              type="button"
              onClick={() => selectSlashCommand(command)}
              className={`w-full px-3 py-2 text-left flex items-center gap-3 transition-colors ${
                index === activeSlashIndex ? 'bg-highlight text-text-primary' : 'hover:bg-highlight/60 text-text-secondary'
              }`}
            >
              <div className="w-8 h-8 rounded-md bg-accent-color/15 text-accent-color flex items-center justify-center font-bold">
                /
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-text-primary">/{command.name}</div>
                <div className="text-xs text-text-muted truncate">{command.description || command.application?.name || 'Application command'}</div>
              </div>
              {command.application?.name && (
                <div className="text-xs text-text-muted">{command.application.name}</div>
              )}
            </button>
          ))}
        </div>
      )}
      
{showMentionSuggestions && suggestions.length > 0 && (
  <div 
    ref={suggestionsRef}
    className="mention-suggestions"
    style={{ bottom: '100%', marginBottom: '4px' }}
  >
    {suggestions.map((user, index) => (
      <div 
        key={user.id}
        className={`mention-suggestion ${index === activeSuggestionIndex ? 'active' : ''}`}
        onClick={() => insertMention(user)}
      >
        <div className="avatar">
          {user.avatar_url ? (
            <img 
              src={user.avatar_url} 
              alt={user.username} 
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <div className="w-full h-full bg-accent-color text-white flex items-center justify-center text-xs rounded-full">
              {user.username.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="info">
          <div className="name">{user.username}</div>
          <div className="status">{user.status || 'offline'}</div>
        </div>
      </div>
    ))}
  </div>
)}
      
      {isEditing && (
        <div className="flex justify-end mt-2 space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSendMessage}
            disabled={!message.trim()}
            className="text-sm text-accent-color hover:text-accent-hover disabled:opacity-50 transition-colors"
          >
            Save Changes
          </button>
        </div>
      )}
      
      {showEmojiPicker && (
        <div 
          ref={emojiPickerRef}
          className="absolute bottom-full mb-2 right-0 z-10"
        >
          <div className="relative">
            <button
              className="absolute top-1 right-1 text-text-secondary hover:text-text-primary z-10 transition-colors"
              onClick={() => setShowEmojiPicker(false)}
            >
              <X className="h-5 w-5" />
            </button>
            <EmojiPicker onEmojiClick={handleEmojiClick} theme={"dark" as any} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageInput;
